// =========================
// Domain-Specific Styles
// =========================

import { flexCol, flexRow, flexRowEnd, flexRowBetween } from "./flex";
import { spaces } from "./spacing";
import { colors } from "./colors";
import { shadows } from "./shadows";
import { body } from "./texts";

// =========================
// UserRoleModal styles
// =========================

export const roleModalPermissionsPaper = (theme: any) => ({
  p: 2.5,
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafbfc'
});

export const roleModalPermissionsHeader = {
  ...flexRowBetween,
  mb: 3
};

export const roleModalPermissionsIconBox = {
  ...flexRow,
  gap: 1
};

export const roleModalPermissionsDescription = (theme: any) => ({
  fontSize: "12px",
  color: theme.palette.text.secondary
});

export const roleModalPermissionsChip = {
  fontWeight: "600"
};

export const roleModalRootPageBox = (theme: any, isEnabled: boolean) => ({
  border: "2px solid",
  borderColor: isEnabled ? theme.palette.primary.main : theme.palette.divider,
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: isEnabled 
    ? theme.palette.background.paper 
    : theme.palette.mode === 'dark' ? '#2a2a2a' : '#f9f9f9',
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: isEnabled ? `0 4px 12px ${theme.palette.primary.main}25` : "none",
  },
});

export const roleModalPageHeader = (theme: any, isEnabled: boolean, hasChildren: boolean) => ({
  ...flexRow,
  alignItems: "center",
  gap: "12px",
  p: "16px",
  backgroundColor: isEnabled 
    ? theme.palette.mode === 'dark' ? '#252525' : '#f0fbfc'
    : theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5',
  cursor: hasChildren ? "pointer" : "default",
  transition: "background-color 0.2s",
  "&:hover": hasChildren ? {
    backgroundColor: theme.palette.mode === 'dark' 
      ? '#2a2a2a' 
      : '#e8f7f9',
  } : {},
});

export const roleModalChevronIcon = (theme: any, isExpanded: boolean, isEnabled: boolean) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  minWidth: "24px",
  height: "24px",
  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
  transition: "transform 0.3s ease",
  opacity: isEnabled ? 1 : 0.5,
  color: theme.palette.primary.main,
});

export const roleModalPageTitle = (theme: any, isEnabled: boolean) => ({
  fontWeight: "700",
  fontSize: "15px",
  color: isEnabled ? theme.palette.text.primary : theme.palette.text.disabled,
  flex: 1,
  transition: "color 0.2s",
});

export const roleModalPermissionBadge = (theme: any, hasPermissions: boolean) => ({
  fontWeight: "600",
  borderColor: hasPermissions ? theme.palette.primary.main : theme.palette.divider,
  color: hasPermissions ? theme.palette.primary.main : theme.palette.text.disabled,
  backgroundColor: hasPermissions ? `${theme.palette.primary.main}10` : "transparent",
});

export const roleModalPagePermissionsBox = (theme: any) => ({
  px: "16px",
  py: "12px",
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`
});

export const roleModalPermissionItem = (isEnabled: boolean) => ({
  ...flexRow,
  alignItems: "center",
  gap: "10px",
  opacity: isEnabled ? 1 : 0.6,
  transition: "opacity 0.2s",
});

export const roleModalPermissionLockIcon = (theme: any) => ({
  fontSize: "16px",
  color: theme.palette.primary.main,
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
});

export const roleModalPermissionText = (theme: any, isEnabled: boolean) => ({
  fontSize: "13px",
  fontWeight: "500",
  color: isEnabled ? theme.palette.text.primary : theme.palette.text.disabled,
  textTransform: "capitalize",
  letterSpacing: "0.3px",
});

export const roleModalChildPagesContainer = (theme: any) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#fafbfc',
  borderTop: `1px solid ${theme.palette.divider}`,
  p: "12px 16px"
});

export const roleModalChildPageBox = (theme: any) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "6px",
  backgroundColor: theme.palette.background.paper,
  overflow: "hidden",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
  },
});

export const roleModalChildPageHeader = (theme: any) => ({
  ...flexRow,
  alignItems: "center",
  gap: "10px",
  p: "12px 14px",
  backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8fafb',
  borderBottom: `1px solid ${theme.palette.divider}`,
});

export const roleModalChildPageTitle = (theme: any) => ({
  fontWeight: "600",
  fontSize: "13px",
  color: theme.palette.text.primary,
  flex: 1,
});

export const roleModalChildPermissionsBox = {
  p: "12px 14px",
  ...flexCol,
  gap: "9px"
};

export const roleModalChildPermissionItem = (isEnabled: boolean) => ({
  ...flexRow,
  alignItems: "center",
  gap: "10px",
  ml: "8px",
  opacity: isEnabled ? 1 : 0.6,
});

export const roleModalChildPermissionLockIcon = (theme: any) => ({
  fontSize: "14px",
  color: theme.palette.primary.main,
  opacity: 0.6,
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
});

export const roleModalChildPermissionText = (theme: any) => ({
  fontSize: "12px",
  fontWeight: "500",
  color: theme.palette.text.primary,
  textTransform: "capitalize",
  letterSpacing: "0.2px",
});

export const roleModalGrandchildPageBox = (theme: any) => ({
  ...roleModalChildPageBox(theme),
  ml: "16px",
});

export const roleModalGrandchildPageHeader = (theme: any) => ({
  ...roleModalChildPageHeader(theme),
});

export const roleModalGrandchildPageTitle = (theme: any) => ({
  ...roleModalChildPageTitle(theme),
});

export const roleModalGrandchildPagesContainer = (theme: any) => ({
  ...roleModalChildPagesContainer(theme),
});

export const roleModalGrandchildPermissionsBox = {
  ...roleModalChildPermissionsBox,
  ml: "8px",
};

export const roleModalGrandchildPermissionItem = (isEnabled: boolean) => ({
  ...roleModalChildPermissionItem(isEnabled),
});

export const roleModalGrandchildPermissionLockIcon = (theme: any) => ({
  ...roleModalChildPermissionLockIcon(theme),
});

export const roleModalGrandchildPermissionText = (theme: any) => ({
  ...roleModalChildPermissionText(theme),
});

export const roleModalDisabledStateBox = (theme: any) => ({
  p: "12px 16px",
  backgroundColor: colors.primaryBlueLight,
  borderTop: `1px solid ${theme.palette.divider}`,
  ...flexRow,
  gap: "8px",
  alignItems: "center",
});

export const roleModalDisabledStateIcon = () => ({
  fontSize: "16px",
  color: "#D97706",
  flex: "0 0 16px",
  width: "16px",
  height: "16px",
  minWidth: "16px",
  minHeight: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
});

export const roleModalDisabledStateText = () => ({
  fontSize: "12px",
  color: "#92400E !important",
  fontWeight: "500",
  "& .MuiTypography-root": {
    color: "#92400E !important"
  }
});

// =========================
// ChapterModal Styles
// =========================

export const chapterModalContainer = {
  ...flexCol,
  gap: spaces[2.5],
};

export const chapterModalHeaderBox = {
  ...flexRowBetween,
};

export const chapterModalStatusBox = {
  ...flexCol,
  borderRadius: "4px",
  padding: `${spaces[0.5]} ${spaces[1]}`,
  backgroundColor: "#fff",
  boxShadow: shadows[1],
};

export const chapterModalStatusTitle = {
  fontFamily: "montserrat",
  fontWeight: "600",
  fontSize: "15px",
};

export const chapterModalFormContainer = {
  ...flexCol,
  gap: spaces[1],
};

export const chapterModalButtonsContainer = {
  ...flexRowEnd,
  gap: spaces[2],
  marginTop: spaces[3],
};

export const chapterModalBudgetBox = {
  ...flexRow,
  gap: spaces[1],
  width: "100%",
};

export const chapterModalSaveButtonProps = {
  labelcolor: "#FFFFFF",
  color: "#0FB0C2",
  hovercolor: "#2FC8B8",
};

export const chapterModalCancelButtonProps = {
  labelcolor: "#FFFFFF",
  color: "#FF4262",
  hovercolor: "#BF4262",
};

// =========================
// Provider Modal: Qualification Card
// =========================

export const qualificationCardContainer = {
  display: 'flex',
  justifyContent: 'flex-start',
  mt: spaces[1.5],
};

export const qualificationCard = {
  width: 'auto',
  minWidth: '280px',
  backgroundColor: colors.white,
  border: `1px solid ${colors.lightBorderOpacity}`,
  boxShadow: shadows.sh1,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    borderColor: colors.primary,
  },
};

export const qualificationCardContent = {
  p: spaces[2],
  '&:last-child': { pb: spaces[2] },
};

export const qualificationHeaderBox = {
  display: 'flex',
  alignItems: 'center',
  gap: spaces[1],
  mb: spaces[1.5],
};

export const qualificationValueBox = {
  display: 'flex',
  alignItems: 'center',
  gap: spaces[1.5],
};

export const qualificationValueText = {
  fontSize: "12px",
  fontWeight: 500,
  color: colors.primary,
  minWidth: '45px',
  fontFamily: 'montserrat',
};

export const qualificationHelpIcon = {
  fontSize: '16px',
  opacity: 0.6,
  color: colors.textSecondary,
  cursor: 'help',
};

// Provider Modal: Status Section
export const providerStatusBox = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '4px',
  padding: '4px 8px',
  backgroundColor: '#fff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
};

export const providerStatusLabel = {
  fontFamily: 'montserrat',
  fontWeight: 600,
  fontSize: '15px',
};

// Provider Modal: Help Icon Span
export const helpIconSpan = {
  cursor: 'help',
  display: 'flex',
  alignItems: 'center',
};

// =========================
// CostCenter & Chapters Views
// =========================

export const costCenterTopBarContainerSx = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  padding: "10px 10px",
  gap: "16px",
};

export const costCenterPapersContainerSx = {
  display: "flex",
  justifyContent: "space-between",
  padding: "10px 10px",
  alignItems: "stretch",
  gap: "16px",
};

export const costCenterFiltersPaperSx = {
  padding: "16px",
  borderRadius: "8px",
  boxShadow: `0 2px 8px ${colors.darkShadowOpacity10}`,
  width: "fit-content",
};

export const costCenterFiltersPaperContentSx = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

export const costCenterSecondaryFiltersSx = {
  display: "flex",
  gap: "16px",
};

export const costCenterSummaryPaperSx = {
  marginBottom: "0",
  padding: "12px 16px",
  borderRadius: "8px",
  boxShadow: `0 2px 8px ${colors.darkShadowOpacity10}`,
  minWidth: "300px",
  maxWidth: "400px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const costCenterSummaryContainerSx = {
  display: "flex",
  flexDirection: "row",
  gap: "24px",
  justifyContent: "center",
  alignItems: "center",
};

export const costCenterSummaryColumnSx = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  paddingRight: "24px",
  borderRight: `1px solid ${colors.lightBorder}`,
  "&:last-child": {
    borderRight: "none",
    paddingRight: "0",
  },
};

export const costCenterResumeLabelSx = {
  fontFamily: "montserrat",
  fontWeight: "600",
  fontSize: "13px",
  color: colors.darkGray2,
};

export const costCenterResumeLabelSmallSx = {
  fontFamily: "montserrat",
  fontWeight: "400",
  fontSize: "13px",
  color: colors.mediumGray2,
};

// =========================
// Documentation helpers
// =========================

export const docManualCard = {
  width: '100%',
  maxWidth: {
    xs: '100%',
    sm: '360px',
  },
  marginLeft: {
    xs: 0,
    sm: 'auto',
  },
  marginRight: {
    xs: 0,
    sm: 'auto',
  },
  height: '100%',
  minHeight: '330px',
  display: 'flex',
  flexDirection: 'column' as const,
  borderRadius: '12px',
  overflow: 'hidden',
  border: `1px solid ${colors.lightBorder}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: 3,
  },
};

export const docCardIconBox = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '92px',
  padding: spaces[2],
  backgroundColor: colors.primary || '#1976d2',
  color: 'white',
};

export const docCardIcon = {
  fontSize: '42px',
};

export const docCardContent = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: spaces[1],
  padding: spaces[2],
  paddingBottom: spaces[1],
};

export const docCardTitle = {
  fontWeight: '700',
  lineHeight: 1.3,
  minHeight: '2.6em',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
};

export const docCardDescription = {
  flex: 1,
  lineHeight: 1.45,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
};

export const docCardDateText = {
  marginTop: 'auto',
  fontSize: '12px',
  opacity: 0.85,
};

export const docCardActions = {
  padding: spaces[2],
  paddingTop: spaces[1],
};

export const docPaperNoPadding = {
  padding: 0,
};

export const docHeaderContainer = {
  padding: spaces[2],
};

export const docTitle = {
  fontWeight: 'bold',
  marginBottom: spaces[2],
};

export const docSubtitle = {
  marginBottom: spaces[3],
};

export const docTypeFilterRow = {
  display: 'flex',
  alignItems: 'center',
  gap: spaces[2],
  flexWrap: 'wrap' as const,
};

export const docTypeSelect = {
  minWidth: '260px',
};

export const docManualGridContainer = {
  padding: spaces[2],
  paddingTop: 0,
};

export const docViewerLayout = {
  display: 'flex',
  flexDirection: 'column' as const,
  height: '100%',
  gap: spaces[2],
  padding: spaces[2],
};

export const docCardButton = {
  width: '100%',
  minHeight: '40px',
  backgroundColor: colors.primary || '#1976d2',
  border: `2px solid ${colors.primary || '#1976d2'}`,
  color: colors.white || '#ffffff',
  fontFamily: 'montserrat',
  fontSize: '13px',
  fontWeight: 'bold',
  borderRadius: '8px',
  letterSpacing: '0.3px',
  textTransform: 'uppercase' as const,
  '&:hover': {
    backgroundColor: colors.primary || '#1976d2',
    color: colors.white || '#ffffff',
  },
};

export const pdfViewerContainer = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  backgroundColor: '#f5f5f5',
};

export const pdfViewerHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: spaces[2],
  backgroundColor: '#ffffff',
  borderBottom: `1px solid ${colors.lightBorder}`,
};

export const pdfViewerContent = {
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'auto',
  position: 'relative' as const,
};

export const pdfViewerIframe = {
  width: '100%',
  height: '100%',
  border: 'none',
};

export const docBackButton = {
  alignSelf: 'flex-start',
  ...body,
  fontWeight: 'bold',
  padding: '10px 20px',
  fontSize: '16px',
  backgroundColor: colors.background,
  color: colors.mediumGray,
  border: `1.5px solid ${colors.mediumBorder}`,
  '&:hover': {
    backgroundColor: colors.veryLightGray,
    border: '1.5px solid #b0b0b0',
  },
};

export const docBackButtonLight = {
  backgroundColor: '#f5f5f5',
  color: '#666666',
  border: '1.5px solid #d0d0d0',
  '&:hover': {
    backgroundColor: '#eeeeee',
    border: '1.5px solid #b0b0b0',
  },
};

export const docBackButtonDark = {
  backgroundColor: '#1a1a1a',
  color: '#a3a3a3',
  border: '1.5px solid #333333',
  '&:hover': {
    backgroundColor: '#262626',
    border: '1.5px solid #444444',
  },
};

export const docPdfContainer = {
  flex: 1,
  minHeight: 0,
  borderRadius: 1,
  overflow: 'hidden',
  border: `1px solid ${colors.lightBorder}`,
};
// =========================
// Job/JobModal styles
// =========================

export const jobModalTitleBox = {
  ...flexRow,
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "8px"
};

export const jobModalTitleBox2 = {
  ...flexCol,
  alignItems: "flex-end"
};

export const jobModalMainContainer = {
  ...flexCol,
  gap: "20px"
};

export const jobModalHeaderBox = {
  ...flexRowBetween,
};

export const jobModalSectionContainer = {
  ...flexCol,
  gap: "12px"
};

export const jobModalFieldLabel = {
  fontSize: "14px",
  fontWeight: "500",
  mb: "4px"
};

export const jobModalButtonsContainer = {
  ...flexRowEnd,
  gap: 2,
  pt: "8px"
};
export const jobModalFormContainer = {
  ...flexCol,
  gap: "16px"
};

export const flexRowAligned = {
  ...flexRow,
  alignItems: "center"
};